import { ColorUtils } from '@common/utils';
import escapeHtml from 'escape-html';
import { CSSProperties } from 'react';
import { Descendant, Element as SlateElement, Text } from 'slate';
import { jsx } from 'slate-hyperscript';
import styleToCss from 'style-object-to-css-string';
import { RichTextBoxUtils as _rtb } from './RichTextBoxUtils';
import {
  RichTextBoxElement,
  RichTextBoxFormatValue,
  RichTextBoxText,
  SerializeRichTextValueToStringResult,
} from './richTextEditorTypes';

export namespace RichTextBoxSerialization {
  /**
   * This helper method's purpose is to join raw html attribute string in such a way that they won't introduce rogue spaces in our html element
   * tags if they are empty.  i.e. to prevent <div > from happening and make sure we get <div> or <div style="color: blue;"></div>
   * Html Element nodes behave funky if they have spaces and might not properly match an alement name case.
   * @param rawAttributes
   * @returns
   */
  const spaceAttributes = (rawAttributes: string[]) => {
    const joinedAttributes = rawAttributes
      .filter((str) => str != null && str.length > 0)
      .join(' ')
      .trim();
    return joinedAttributes && joinedAttributes.length > 0
      ? ` ${joinedAttributes}`
      : '';
  };

  /**
   * This takes a CSSProperties object and converts it into a string for a raw html styles tag
   * which is useful for manually building html in string manipulation and is used in serializeRichTextToHtml
   * @param css
   * @returns
   */
  const getStyleAttribute = (css: CSSProperties) => {
    let styleString = styleToCss(css);
    return styleString && styleString.length > 0
      ? `style="${styleString}"`
      : '';
  };

  /**
   * Takes a JSX HtmlElement and serializes it into the custom Slate Schema for the RichTextBoxComponent
   * The raw html strings must first be parsed into a JSX Object Tree which happens in parseHtmlToRichText
   * and is why this method is not exported.
   * @param el
   * @param markAttributes
   * @returns
   */
  const deserializeRichTextFromHtml = (
    el: HTMLElement | ChildNode,
    previousDescendant: Descendant[] | null,
    blockMarks: Partial<RichTextBoxElement> = {},
    textMarks: Partial<RichTextBoxText> = {},
  ): Descendant[] | null => {
    if (el.nodeType === Node.TEXT_NODE) {
      let text = el.textContent ?? '';
      text = text.replace(/\u200B/g, ''); //<- clean text
      return [jsx('text', textMarks, text)];
    } else if (el.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    //el will be an HTMLElement past this point because it returns above if it's not an Element or text node
    const element = el as HTMLElement;

    if (el.nodeType === Node.ELEMENT_NODE && element.style) {
      if (element.style.marginLeft) {
        const indentationLevel = parseInt(element.style.marginLeft);
        if (indentationLevel) blockMarks.indentationLevel = indentationLevel;
      }
      if (element.style.color) {
        textMarks.color =
          element.style.color.indexOf('rgb') > -1
            ? ColorUtils.rgbToHex(element.style.color)
            : element.style.color;
      }
    }

    //bold, italic etc is handled with native html elements instead of styles to be better compliant to email/pdf
    //this will process the node name and set the format bit for it and then return from the recursion to finish
    //building the previous element object
    switch (element.nodeName.toUpperCase()) {
      case 'BR':
        if (previousDescendant !== null && previousDescendant.length) {
          //if there was a previous element and it was a text node then we'll add a new line to the end of it
          //so it matches how the editor builds the live schema.
          const lastDescendant = _.last(previousDescendant);
          if (lastDescendant && Text.isText(lastDescendant)) {
            lastDescendant.text = `${lastDescendant.text}\n`;
            return null; //<- this element should be a void element, we added it to the previous element.
          }
        }
        return [jsx('text', {}, '\n')]; //<- don't need any marks on a newline break
      case 'STRONG':
        textMarks.format =
          (textMarks.format ?? 0) | RichTextBoxFormatValue.bold;
        break;
      case 'I':
        textMarks.format =
          (textMarks.format ?? 0) | RichTextBoxFormatValue.italic;
        break;
      case 'U':
        textMarks.format =
          (textMarks.format ?? 0) | RichTextBoxFormatValue.underline;
        break;
      default:
        break;
    }

    //deduces all the children of this element if any and returns here, recursive, no recursion will get past this point unless it didn't have children, and the child will be an element at
    //that point
    const children = Array.from(element.childNodes)
      .filter((a) => a !== null)
      .map((node) => {
        previousDescendant = deserializeRichTextFromHtml(
          node,
          previousDescendant,
          blockMarks,
          textMarks,
        );
        return previousDescendant;
      })
      .filter((a) => a !== null)
      .flat() as Descendant[];

    //if there are no children then we set an empty text element, slate requires that any element in the dom have at least 1 child with empty text.
    //i.e. if there are 5 block level elements in the dom each one must have a child with empty text and cannot have a null or undefined child, everything
    //must have at least 1 child text node, and text nodes do not have children, but an eleemnt can have multiple child text nodes.
    if (children.length === 0) {
      children.push(jsx('text', textMarks, ''));
    }

    let blockElement: Descendant[] = children;
    //recursion made it past children loops and is processing the block level element now
    switch (element.nodeName) {
      case 'BODY':
        return jsx('fragment', {}, children);
      case 'OL':
        blockElement = [
          jsx('element', { type: 'numbered-list', ...blockMarks }, children),
        ];
        break;
      case 'UL': {
        blockElement = [
          jsx('element', { type: 'bulleted-list', ...blockMarks }, children),
        ];
        break;
      }
      case 'LI': {
        blockElement = [
          jsx('element', { type: 'list-item', ...blockMarks }, children),
        ];
        break;
      }
      case 'P':
        blockElement = [
          jsx('element', { type: 'paragraph', ...blockMarks }, children),
        ];
        break;
      default: {
        blockElement = children;
        break;
      }
    }
    //clear text marks, done rendering those children
    if (textMarks.format) delete textMarks.format;
    if (textMarks.color) delete textMarks.color;
    return blockElement;
  };

  /**
   * Serializes our custom Slate Schema into raw HTML
   * @param node You can pass the value attribute from RichTextBox's onChange handler to this.  Node is a merged ts type from slate
   * that uses declaration merging with our types.
   * @returns A serialized Slate Tree to HTML
   * TODO: Can we generate this as JSX instead and then convert the JSX to html via some react utility?  React obviously already does this.
   * Also TODO: When/if/where should we consider DOMPurify to purify our HTML?  Incoming third party notes etc could be dangerous for certain attacks.
   */
  const serializeRichTextToHtml = (node: Descendant): string => {
    //The node is text
    if (Text.isText(node)) {
      let string = escapeHtml(node.text) ?? '';
      string = string.replace(/\u200B/g, ''); //<- remove unicode zero width space
      string = node.text.replace(/\r?\n|\r/g, '<br />');
      string = string ?? '<br />';

      const format = node.format ?? RichTextBoxFormatValue.none;
      const styles: CSSProperties = {};

      if (node.color)
        //we need to ensure attributes are omitted, not undefined
        styles.color = node.color;

      //convert any styles to a string, this includes the attribute tag itself so we don't introduce rogue spaces in our elements
      const styleString = getStyleAttribute(styles);

      //if we have attribute tags, this properly spaces them
      const attributeString = spaceAttributes([styleString]);

      //this processes format bit flags, if set it will concatenate the string with the relevant
      //native element for bold, italics, etc.
      if (
        (format & RichTextBoxFormatValue.bold) ===
        RichTextBoxFormatValue.bold
      ) {
        string = `<strong>${string}</strong>`;
      }
      if (
        (format & RichTextBoxFormatValue.italic) ===
        RichTextBoxFormatValue.italic
      ) {
        string = `<i>${string}</i>`;
      }
      if (
        (format & RichTextBoxFormatValue.underline) ===
        RichTextBoxFormatValue.underline
      ) {
        string = `<u>${string}</u>`;
      }
      return `<span${attributeString}>${string}</span>`;
    }

    //similar to children in the deserialize loop, but backwards, we are serializing all the children
    //just building am html string representing the whole json schema.
    const children =
      node.children == null
        ? null
        : node.children.map((n) => serializeRichTextToHtml(n)).join('');

    //we need to generate html for all of our Rich Text Box elements
    if (SlateElement.isElement(node)) {
      const styles: CSSProperties = {};

      //using a custom attribute to determine indentation since styling could be inconsistent and produce false positives
      if (node.indentationLevel)
        styles.marginLeft = `${node.indentationLevel}rem`;

      const styleString = getStyleAttribute(styles);
      const sharedAttributes = spaceAttributes([styleString]);

      switch (node.type) {
        case 'bulleted-list': {
          return `<ul${sharedAttributes}>${children}</ul>`;
        }
        case 'numbered-list': {
          return `<ol${sharedAttributes}>${children}</ol>`;
        }
        case 'list-item': {
          return `<li${sharedAttributes}>${children}</li>`;
        }
        case 'paragraph':
          return `<p${sharedAttributes}>${children}</p>`;
        default:
          return children ?? '';
      }
    }
    //ensure any schema we don't support generates an empty html string
    return '';
  };

  /**
   * Internal function, Use DOMParser to load a dom from the html
   * this is what allows us to walk through it in the deserializer.
   * @param htmlString An html or plain text string, text/html is tolerant of both
   * @returns A ChildNode from the DomParser, could be text or element etc.
   */
  const parseHtml = (htmlString: string | undefined | null) => {
    let parser = new DOMParser();
    let doc = parser.parseFromString(htmlString ?? '', 'text/html');

    const elements: ChildNode[] = [];

    if (doc.body.childNodes.length > 0) {
      doc.body.childNodes.forEach((node) => {
        if (
          node.nodeType === Node.TEXT_NODE &&
          node.textContent != null &&
          !_.isEmpty(_.trim(node.textContent))
        ) {
          const ret = doc.createElement('p');
          ret.appendChild(node);
          elements.push(ret);
        } else elements.push(node);
      });
    } else {
      //there was no html to load, this will ensure we have an empty text node via a span
      const ret = doc.createElement('p');
      elements.push(ret);
    }
    return elements;
  };

  /**
   * Basically what the parseHtml internal function does but will handle creating a default node if the html doesn't parse or is empty.
   * Slate requires that the editor always have at least 1 block level element with empty text in it in order to function and not break.
   * @param html
   * @returns
   */
  export const parseHtmlToRichText = (html: string | undefined | null) => {
    const htmlElements = parseHtml(html);
    const ret: Descendant[] = [];
    if (htmlElements.length > 0) {
      //there are multiple block level elements possible so we need to deserialize each one.
      htmlElements.forEach((element) => {
        const descendant = deserializeRichTextFromHtml(element, null);
        if (descendant) ret.push(...descendant); //descontruct the children flatly, there will only be one per loop.
      });
    }
    if (ret.length === 0)
      ret.push({ type: 'paragraph', children: [{ text: '' }] });

    return ret;
  };

  /**
   * This will serialize our Schema into html if it needs to be html and plain text if it can be plain text
   * This allows us to prevent causing RHF isDirty to break on existing plain text notes.
   * @param nodes
   * @returns An object with a value string and a bollean indicating if it's plain text or not.
   */
  export const serializeRichTextValueToString = (
    nodes: Descendant[],
    forceHtml?: boolean,
    isSMStext?: boolean,
  ): SerializeRichTextValueToStringResult => {
    if (!forceHtml && _rtb.canConvertDescendantToPlainText(nodes))
      return {
        isPlainText: true,
        value: isSMStext
          ? _rtb.serializeRichTextToPlainTextNoHtml(nodes)
          : _rtb.serializeRichTextToPlainText(nodes),
      };
    let richHtmlStringNodes =
      nodes.map((node) => serializeRichTextToHtml(node))?.join('') ?? '';
    return {
      isPlainText: false,
      value: richHtmlStringNodes,
    };
  };
}
